import type { HandOffConversation, HandoffResolvedPeriod, HandoffTab } from '../../types/handoff';
import { HandoffQueueItem } from './HandoffQueueItem';

type Props = {
  activeTab: HandoffTab;
  counts: Record<HandoffTab, number>;
  conversations: HandOffConversation[];
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onChangeTab: (tab: HandoffTab) => void;
  resolvedPeriod: HandoffResolvedPeriod;
  onChangeResolvedPeriod: (period: HandoffResolvedPeriod) => void;
  mailOnly: boolean;
  onChangeMailOnly: (value: boolean) => void;
  mailCount: number;
};

const TABS: { value: HandoffTab; label: string }[] = [
  { value: 'requested', label: 'Nya' },
  { value: 'mine', label: 'Mina' },
  { value: 'active', label: 'Aktiva' },
  { value: 'resolved', label: 'Lösta' },
];

const PERIODS: { value: HandoffResolvedPeriod; label: string }[] = [
  { value: 'today', label: 'Idag' },
  { value: '7d', label: '7 dagar' },
  { value: '30d', label: '30 dagar' },
  { value: 'all', label: 'Alla' },
];

export function HandoffQueuePanel({
  activeTab,
  counts,
  conversations,
  selectedConversationId,
  onSelectConversation,
  onChangeTab,
  resolvedPeriod,
  onChangeResolvedPeriod,
  mailOnly,
  onChangeMailOnly,
  mailCount,
}: Props) {
  return (
    <section className="flex min-h-0 flex-col border-r bg-white">
      <div className="flex flex-wrap gap-1 border-b px-3 py-3">
        {TABS.map(({ value, label }) => {
          const isActive = value === activeTab;
          return (
            <button
              key={value}
              type="button"
              onClick={() => onChangeTab(value)}
              className={[
                'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition',
                isActive ? 'bg-gray-950 text-white' : 'text-gray-500 hover:bg-gray-100',
              ].join(' ')}
            >
              <span>{label}</span>
              <span
                className={[
                  'rounded-full px-1.5 text-[10px]',
                  isActive ? 'bg-white/20' : 'bg-gray-100',
                ].join(' ')}
              >
                {counts[value]}
              </span>
            </button>
          );
        })}
      </div>

      {activeTab === 'resolved' && (
        <div className="flex flex-wrap gap-1 border-b bg-gray-50 px-3 py-2">
          {PERIODS.map(({ value, label }) => {
            const isActive = value === resolvedPeriod;
            return (
              <button
                key={value}
                type="button"
                onClick={() => onChangeResolvedPeriod(value)}
                className={[
                  'rounded-md px-2.5 py-1 text-[11px] font-medium transition',
                  isActive
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                    : 'text-gray-500 hover:text-gray-800',
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {activeTab !== 'resolved' && (
        <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
          <label className="flex cursor-pointer items-center gap-2 text-[11px] font-medium text-gray-600">
            <input
              type="checkbox"
              checked={mailOnly}
              onChange={(event) => onChangeMailOnly(event.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300"
            />
            Visa endast mail-handoffs
          </label>
          {mailCount > 0 && (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700">
              {mailCount}
            </span>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">Inga ärenden här.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {conversations.map((conversation) => (
              <HandoffQueueItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === selectedConversationId}
                onSelect={() => onSelectConversation(conversation.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
